/**
 * Attribute-related mutations for the Command Engine.
 *
 * Covers Phase 2.1 (ATTDEF authoring) and Phase 2.2 (DDATTE / ATTEDIT /
 * EATTEDIT — editing ATTRIB values and per-ATTRIB overrides on existing
 * INSERTs). The file is split from blockMutations.ts which already
 * exceeds 1000 LOC; public symbols are re-exported through
 * geometryMutations.ts so callers keep their existing import path
 * (`@dxf-ai-editor/command-engine`).
 *
 * Public surface (all re-exported by geometryMutations.ts):
 *   - createAttdefMutation(input) -> CreateAttdefResult
 *   - updateAttributeValueMutation(input) -> UpdateAttributeValueResult
 *   - updateAttributeValuesMutation(input) -> UpdateAttributeValuesResult
 *
 * Design notes
 * ------------
 * 1. createAttdefMutation is pure — it accepts AutoCAD-parity ATTDEF
 *    field set, validates them (tag non-empty, height > 0, oblique
 *    range, justification range, alignmentPoint required when h/vJ != 0)
 *    and returns a fully-populated AttdefEntity. The reducer inserts
 *    the entity into blockDef.entities; the bidirectional mirror in
 *    blockEditReducer keeps the block-edit space in sync automatically.
 *
 * 2. updateAttributeValueMutation matches the ATTRIB by `tag` inside
 *    insert.attributes. If the tag is not present — it looks up the
 *    corresponding ATTDEF inside blockDefinition.entities and, when
 *    non-constant, creates a new ATTRIB entry inheriting defaults from
 *    the ATTDEF. Constant ATTDEFs throw — editors must hide them.
 *
 * 3. updateAttributeValuesMutation is a thin wrapper around the single
 *    update so the DDATTE / EATTEDIT "Save" button can apply all
 *    changes in one pass; the reducer performs one patch per update.
 */

import { coerceColor } from "@dxf-ai-editor/drawing-model";
import type {
  AttdefEntity,
  BlockDefinition,
  BlockInsertAttribute,
  BlockInsertEntity as DrawingBlockInsertEntity,
  EntityColor,
} from "@dxf-ai-editor/drawing-model";

// ═══════════════════════════════════════════════════════════════
// createAttdefMutation
// ═══════════════════════════════════════════════════════════════

export type AttdefFlagsInput = {
  invisible?: boolean;
  constant?: boolean;
  verify?: boolean;
  preset?: boolean;
  lockPosition?: boolean;
  multiline?: boolean;
};

export interface CreateAttdefInput {
  /** Id to assign to the new AttdefEntity. */
  nextId: string;
  /** The block-edit space id (AttdefEntity requires a spaceId). */
  spaceId: string;
  /** Target block. Must match the active BEDIT session's block. */
  blockName: string;
  attdef: {
    tag: string;
    prompt: string;
    defaultValue: string;
    insertionPoint: { x: number; y: number };
    height: number;
    rotation: number;
    widthFactor: number;
    obliqueAngle: number;
    styleName: string;
    hJustification: number;
    vJustification: number;
    alignmentPoint?: { x: number; y: number };
    flags: AttdefFlagsInput;
    backgroundFill?: boolean;
    backgroundColor?: string;
    fillBoxScale?: number;
  };
  layer: string;
  /**
   * CECOLOR — accepts the legacy color string (ACI / ByLayer / ByBlock) OR the
   * typed `EntityColor` union stamped at the dispatch chokepoint (round-5
   * FINDING B). `coerceColor` is idempotent for both, and `undefined` (no stamp
   * — legacy / tests) coerces to ByLayer.
   */
  color: string | EntityColor | undefined;
  linetype: string;
  lineweight: string;
  transparency?: string;
}

export interface CreateAttdefResult {
  /** Block name the new ATTDEF will be inserted into. */
  blockName: string;
  /** Fully-constructed AttdefEntity ready for insertion. */
  attdefEntity: AttdefEntity;
  summary: string;
}

export type AttdefValidationError =
  | "empty-tag"
  | "whitespace-in-tag"
  | "non-positive-height"
  | "oblique-out-of-range"
  | "h-justification-out-of-range"
  | "v-justification-out-of-range"
  | "missing-alignment-point";

/**
 * Validate ATTDEF inputs. Returns null when acceptable, otherwise a
 * structured reason code the caller can localise. The caller MUST run
 * this before dispatching the reducer command.
 */
export function validateAttdefInput(
  input: CreateAttdefInput,
): AttdefValidationError | null {
  const { attdef } = input;
  if (attdef.tag.trim().length === 0) return "empty-tag";
  // AutoCAD warns (but allows) whitespace in tags. We keep the
  // permissive behaviour — return null. The caller can surface a UI
  // warning separately. Only reject if the tag is pure whitespace.
  if (attdef.height <= 0) return "non-positive-height";
  if (attdef.obliqueAngle < -85 || attdef.obliqueAngle > 85) {
    return "oblique-out-of-range";
  }
  if (attdef.hJustification < 0 || attdef.hJustification > 5) {
    return "h-justification-out-of-range";
  }
  if (attdef.vJustification < 0 || attdef.vJustification > 3) {
    return "v-justification-out-of-range";
  }
  const needsAlignmentPoint =
    attdef.hJustification !== 0 || attdef.vJustification !== 0;
  if (needsAlignmentPoint && !attdef.alignmentPoint) {
    return "missing-alignment-point";
  }
  // `whitespace-in-tag` intentionally never returned — AutoCAD permits it.
  return null;
}

/**
 * Create a new AttdefEntity from AutoCAD-parity inputs. Validates via
 * `validateAttdefInput` and throws on failure. The returned entity is
 * unvalidated by the zod schema on purpose — callers (the reducer)
 * pipe the value through `assembleDrawingDocument` which handles
 * the final defensive check (Zod parse in dev, invariants check in prod).
 */
export function createAttdefMutation(
  input: CreateAttdefInput,
): CreateAttdefResult {
  const validationError = validateAttdefInput(input);
  if (validationError) {
    throw new Error(`Invalid ATTDEF input: ${validationError}`);
  }
  const { attdef } = input;

  const attdefEntity: AttdefEntity = {
    id: input.nextId,
    spaceId: input.spaceId,
    layer: input.layer,
    // EPIC-COLOR-UNION S1 — the AttdefEntity `color` field is the typed
    // `EntityColor` union; coerce the legacy-string command input (ACI /
    // ByLayer / ByBlock) into it. Idempotent, so an already-typed value
    // passes through unchanged.
    color: coerceColor(input.color),
    linetype: input.linetype,
    lineweight: input.lineweight,
    transparency: input.transparency ?? "0",
    type: "Attdef",
    handle: "",
    tag: attdef.tag,
    prompt: attdef.prompt,
    defaultValue: attdef.defaultValue,
    insertionPoint: {
      x: attdef.insertionPoint.x,
      y: attdef.insertionPoint.y,
    },
    ...(attdef.alignmentPoint
      ? {
          alignmentPoint: {
            x: attdef.alignmentPoint.x,
            y: attdef.alignmentPoint.y,
          },
        }
      : {}),
    height: attdef.height,
    rotation: attdef.rotation,
    styleName: attdef.styleName,
    widthFactor: attdef.widthFactor,
    obliqueAngle: attdef.obliqueAngle,
    generationFlags: 0,
    hJustification: attdef.hJustification,
    vJustification: attdef.vJustification,
    flags: {
      invisible: Boolean(attdef.flags.invisible),
      constant: Boolean(attdef.flags.constant),
      verify: Boolean(attdef.flags.verify),
      preset: Boolean(attdef.flags.preset),
      lockPosition: Boolean(attdef.flags.lockPosition),
      multiline: Boolean(attdef.flags.multiline),
    },
    backgroundFill: Boolean(attdef.backgroundFill),
    ...(attdef.backgroundColor
      ? { backgroundColor: attdef.backgroundColor }
      : {}),
    fillBoxScale: attdef.fillBoxScale ?? 1.5,
    annotativeContexts: [],
  };

  return {
    blockName: input.blockName,
    attdefEntity,
    summary: `ATTDEF ${attdef.tag} created in ${input.blockName} at ${attdef.insertionPoint.x},${attdef.insertionPoint.y}`,
  };
}

// ═══════════════════════════════════════════════════════════════
// updateAttributeValueMutation (DDATTE / ATTEDIT / EATTEDIT single tag)
// ═══════════════════════════════════════════════════════════════

export interface AttributeOverridesInput {
  overrideHeight?: number;
  overrideRotation?: number;
  overridePosition?: { x: number; y: number };
  overrideStyle?: string;
  invisible?: boolean;
}

export interface UpdateAttributeValueInput {
  /** INSERT being edited (must live in the current document). */
  insert: DrawingBlockInsertEntity;
  /** Referenced BlockDefinition (must exist). */
  blockDefinition: BlockDefinition;
  /** ATTRIB tag. Matched case-sensitively (AutoCAD uppercases tags but
   *  editors keep the user's casing). */
  tag: string;
  /** New value. Undefined keeps the existing value (useful when only
   *  overrides change). Empty string clears the value. */
  newValue?: string;
  /** Optional property overrides on the ATTRIB. */
  overrides?: AttributeOverridesInput;
}

export interface UpdateAttributeValueResult {
  /** Updated attributes array ready to write back to the INSERT. */
  attributes: BlockInsertAttribute[];
  /** True when a new ATTRIB entry was created (previously absent). */
  created: boolean;
  summary: string;
}

/**
 * Update a single ATTRIB on an INSERT. If the tag is not present, look
 * up the matching ATTDEF and (when non-constant) create a new ATTRIB
 * inheriting defaults from the ATTDEF. Throws when the tag refers to
 * a constant ATTDEF (constants cannot be edited via DDATTE / EATTEDIT).
 */
export function updateAttributeValueMutation(
  input: UpdateAttributeValueInput,
): UpdateAttributeValueResult {
  const { insert, blockDefinition, tag, newValue, overrides } = input;

  // Locate the corresponding ATTDEF — both for constant/invisible
  // checks and to pick inheritable defaults when we need to create a
  // new ATTRIB entry.
  const matchingAttdef = blockDefinition.entities.find(
    (child): child is AttdefEntity =>
      child.type === "Attdef" && child.tag === tag,
  );

  if (matchingAttdef && matchingAttdef.flags.constant) {
    throw new Error(
      `Cannot edit constant attribute "${tag}" — create a new INSERT to change its value.`,
    );
  }

  const existingIndex = insert.attributes.findIndex((a) => a.tag === tag);
  let created = false;

  let nextAttributes: BlockInsertAttribute[];
  if (existingIndex === -1) {
    if (!matchingAttdef) {
      // Tag is not present as ATTRIB and there is no ATTDEF template —
      // this is an inconsistent request; throw so the UI can surface
      // an error instead of silently inventing a tag.
      throw new Error(
        `Attribute "${tag}" not found on INSERT and no ATTDEF template exists.`,
      );
    }
    // Create a new ATTRIB inheriting ATTDEF defaults. value falls back
    // to the ATTDEF default (or empty if newValue is explicitly undefined).
    //
    // round-3 FINDING 1+4 — `overridePosition` is seeded ONLY when the
    // caller supplied a genuine EATTEDIT reposition
    // (`overrides.overridePosition`), never as a synthetic default. A
    // value-only edit (DDATTE / the properties panel changing just the
    // attribute text — every caller that omits `overrides.overridePosition`)
    // must NOT stamp a position override at all: `overridePosition` is
    // OPTIONAL on `blockInsertAttributeSchema`, and the render path
    // (`attdefFromEntity.ts resolvePlacement`) already falls back to
    // `resolveTemplateAnchor(attdef)` — which resolves to `alignmentPoint`
    // for a justified ATTDEF, `insertionPoint` otherwise — when no override
    // is present. Unconditionally seeding this field from the ATTDEF's raw
    // `insertionPoint` (as this used to) made every freshly-created ATTRIB
    // row carry a synthetic "override" that the render path could no longer
    // distinguish from a real EATTEDIT reposition, so a justified ATTDEF's
    // correct alignment-point anchor (visible before the first value edit,
    // since no ATTRIB row existed yet) reverted to the raw insertion point
    // as soon as the user typed a value — losing the alignment. The other
    // override fields (height/rotation/style) are NOT affected by this same
    // bug: their seeded default (`matchingAttdef.height` / `.rotation` /
    // `.styleName`) is IDENTICAL to what `resolvePlacement`'s own fallback
    // would resolve to when the field is left undefined, so seeding them
    // unconditionally is behaviourally inert either way.
    const newAttrib: BlockInsertAttribute = {
      tag,
      value: newValue ?? matchingAttdef.defaultValue,
      handle: "",
      ...(overrides?.overridePosition !== undefined
        ? {
            overridePosition: {
              x: overrides.overridePosition.x,
              y: overrides.overridePosition.y,
            },
          }
        : {}),
      overrideHeight: overrides?.overrideHeight ?? matchingAttdef.height,
      overrideRotation: overrides?.overrideRotation ?? matchingAttdef.rotation,
      overrideStyle: overrides?.overrideStyle ?? matchingAttdef.styleName,
      invisible: overrides?.invisible ?? matchingAttdef.flags.invisible,
    };
    nextAttributes = [...insert.attributes, newAttrib];
    created = true;
  } else {
    nextAttributes = insert.attributes.map((a, idx) => {
      if (idx !== existingIndex) return a;
      const updated: BlockInsertAttribute = {
        ...a,
        value: newValue ?? a.value,
      };
      if (overrides) {
        if (overrides.overrideHeight !== undefined) {
          updated.overrideHeight = overrides.overrideHeight;
        }
        if (overrides.overrideRotation !== undefined) {
          updated.overrideRotation = overrides.overrideRotation;
        }
        if (overrides.overridePosition !== undefined) {
          updated.overridePosition = {
            x: overrides.overridePosition.x,
            y: overrides.overridePosition.y,
          };
        }
        if (overrides.overrideStyle !== undefined) {
          updated.overrideStyle = overrides.overrideStyle;
        }
        if (overrides.invisible !== undefined) {
          updated.invisible = overrides.invisible;
        }
      }
      return updated;
    });
  }

  return {
    attributes: nextAttributes,
    created,
    summary: `ATTRIB ${tag} ${created ? "created" : "updated"} on INSERT ${insert.id}`,
  };
}

// ═══════════════════════════════════════════════════════════════
// updateAttributeValuesMutation (batch — DDATTE/EATTEDIT "Save")
// ═══════════════════════════════════════════════════════════════

export interface UpdateAttributeValuesInput {
  insert: DrawingBlockInsertEntity;
  blockDefinition: BlockDefinition;
  updates: Array<{
    tag: string;
    newValue?: string;
    overrides?: AttributeOverridesInput;
  }>;
}

export interface UpdateAttributeValuesResult {
  attributes: BlockInsertAttribute[];
  createdTags: string[];
  summary: string;
}

/**
 * Apply multiple ATTRIB updates sequentially. Mirrors AutoCAD's
 * DDATTE / EATTEDIT "Save" which commits every visible row at once.
 * Constant tags are rejected per-update; the first constant tag that
 * slips through throws. Editors should filter constants out of the
 * update list before calling this function.
 */
export function updateAttributeValuesMutation(
  input: UpdateAttributeValuesInput,
): UpdateAttributeValuesResult {
  let attributes = input.insert.attributes;
  const createdTags: string[] = [];

  for (const update of input.updates) {
    const interim = updateAttributeValueMutation({
      insert: { ...input.insert, attributes },
      blockDefinition: input.blockDefinition,
      tag: update.tag,
      ...(update.newValue !== undefined ? { newValue: update.newValue } : {}),
      ...(update.overrides ? { overrides: update.overrides } : {}),
    });
    attributes = interim.attributes;
    if (interim.created) createdTags.push(update.tag);
  }

  return {
    attributes,
    createdTags,
    summary: `Updated ${input.updates.length} attribute${input.updates.length === 1 ? "" : "s"} on INSERT ${input.insert.id}`,
  };
}
